<script setup>
import { computed, useSlots, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ContactAPI from 'dashboard/api/contacts';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  useMapGetter,
} from 'dashboard/composables/store.js';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';


const props = defineProps({
  selectedContact: {
    type: Object,
    default: () => ({}),
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['goToContactsList', 'toggleBlock']);

const { t } = useI18n();
const slots = useSlots();
const route = useRoute();
const router = useRouter();
const store = useStore();

const { isAdmin } = useAdmin();
const currentUser = useMapGetter('getCurrentUser');

const isContactSidebarOpen = ref(false);

const contactId = computed(() => route.params.contactId);

const selectedContactName = computed(() => {
  return props.selectedContact?.name;
});

const conversationId = ref(null);
const conversation = ref({
  id: null,
  meta: {
    assignee: null,
  },
});

const fetchConversationId = async () => {
  try {
    const response = await ContactAPI.getConversations(contactId.value);
    conversation.value = response.data.payload[0]; // asumimos que tomas la primera conversación
    if (conversation.value) {
      conversationId.value = conversation.value.id;
    }
  } catch (e) {
    console.error('Error al obtener conversación del contacto', e);
  }
};

onMounted(fetchConversationId);

const navigateToConversation = () => {
   const url = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      activeInbox: null,
      id: conversationId.value
    })
  );
  router.push(url);
}
function showError(message) {
  useAlert(message );
}
const openConversation = async () => {
  //reload conversation
  await fetchConversationId();
  try {
    //group
    if (conversation.value.meta?.sender?.identifier?.endsWith('@g.us')){
      return navigateToConversation();
    }

    if (isAdmin.value) {
      return navigateToConversation();
    }

    const assignee = conversation.value.meta?.assignee

    //if the conversation is already assigned to the current user,
    if (assignee && assignee.id === currentUser.value.id) {
      return navigateToConversation();
    }
    //if the conversation is assigned to another agent,
    if (assignee && assignee.id !== currentUser.value.id) {
      showError(t('CONVERSATION.SEARCH.ALREADY_ASSIGNED_MESSAGE'));
      return
    }

    //agent get the oldest unassigned open conversation
     const oldestId = await store.dispatch('fetchOldestUnassignedConversation');

    if ( conversation.value.status === 'open' && (!oldestId || conversation.value.id !== oldestId) ){
      useAlert(t('CONVERSATION.ERROR_ONLY_OLDEST'));
      return;
    }

    //if the conversation is unassigned, self-assign it to the current user
    if (!assignee && conversation.value.status === 'open') {
      //assign to agent only if the conversation is open
      onClickSelfAssign(conversation.value);
      return navigateToConversation();
    }

    //if the conversation is not open
    if (conversation.value.status !== 'open') {
      return navigateToConversation();
    }

    //TODO: Validte what should happend with the peding status

  } catch (err) {
     showError(t('CONVERSATION.SEARCH.CONVERSATION_OPEN_ERROR'));
     console.error('Error al abrir conversación', err);
  }
}

const onClickSelfAssign = (chat) => {

  const {
    account_id,
    availability_status,
    available_name,
    email,
    id,
    name,
    role,
    avatar_url,
  } = currentUser.value;

  const selfAssign = {
    account_id,
    availability_status,
    available_name,
    email,
    id,
    name,
    role,
    thumbnail: avatar_url,
  };

  //this.$set(this.isLoadingAssign, chat.id, true);

  // 1. Actualiza localmente el chat si tienes lógica para eso
  if(chat.meta) {
    chat.meta.assignee = selfAssign;
  }

  // 2. Envía a Vuex
  //this.$store.dispatch('setCurrentChatAssignee', this.currentUser);
  store
    .dispatch('assignAgent', {
      conversationId: chat.id,
      agentId: id,
    })
    .then(() => {
      this.$toast && this.$toast.success(this.$t('CONVERSATION.CHANGE_AGENT'));
    })
    .finally(() => {
      // this.$set(this.isLoadingAssign, chat.id, false);
    });
}

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS'),
      link: '#',
    },
  ];
  if (props.selectedContact) {
    items.push({
      label: selectedContactName.value,
    });
  }
  return items;
});

const isContactBlocked = computed(() => {
  return props.selectedContact?.blocked;
});

const handleBreadcrumbClick = () => {
  emit('goToContactsList');
};

const toggleBlock = () => {
  emit('toggleBlock', isContactBlocked.value);
};

const handleConversationSidebarToggle = () => {
  isContactSidebarOpen.value = !isContactSidebarOpen.value;
};

const closeMobileSidebar = () => {
  if (!isContactSidebarOpen.value) return;
  isContactSidebarOpen.value = false;
};
</script>

<template>
  <section
    class="flex w-full h-full overflow-hidden justify-evenly bg-n-surface-1"
  >
    <div
      class="flex flex-col w-full h-full transition-all duration-300 ltr:2xl:ml-56 rtl:2xl:mr-56"
    >
      <header class="sticky top-0 z-10 px-6 3xl:px-0">
        <div class="w-full mx-auto max-w-[40.625rem]">
          <div
            class="flex flex-col xs:flex-row items-start xs:items-center justify-between w-full py-7 gap-2"
          >
            <Breadcrumb
              :items="breadcrumbItems"
              @click="handleBreadcrumbClick"
            />
            <div class="flex items-center gap-2">
              <Button
                :label="
                  !isContactBlocked
                    ? $t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
                    : $t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
                "
                size="sm"
                slate
                :is-loading="isUpdating"
                :disabled="isUpdating"
                @click="toggleBlock"
              />
              <VoiceCallButton
                :phone="selectedContact?.phoneNumber"
                :contact-id="contactId"
                :label="$t('CONTACT_PANEL.CALL')"
                size="sm"
              />
              <!-- Send new message modal-->
          <!-- <ComposeConversation :contact-id="contactId">
                <template #trigger="{ toggle }">
                  <Button
                    :label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
                    size="sm"
                    @click="toggle"
                  />
                </template>
              </ComposeConversation> -->
                <Button
                  :label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
                  size="sm"
                  @click="openConversation"
                />

            </div>
          </div>
        </div>
      </header>
      <main class="flex-1 px-6 overflow-y-auto 3xl:px-px">
        <div class="w-full py-4 mx-auto max-w-[40.625rem]">
          <slot name="default" />
        </div>
      </main>
    </div>
    <Banner
      v-if="true"
      color-scheme="alert"
      :banner-message="$t('CONTACTS_LAYOUT.HEADER.VIEW_CONVERSATION')"
    ></Banner>

    <!-- Desktop sidebar -->
    <div
      v-if="slots.sidebar"
      class="hidden lg:block overflow-y-auto justify-end min-w-52 w-full py-6 max-w-md border-l border-n-weak bg-n-solid-2"
    >
      <slot name="sidebar" />
    </div>

    <!-- Mobile sidebar container -->
    <div
      v-if="slots.sidebar"
      class="lg:hidden fixed top-0 ltr:right-0 rtl:left-0 h-full z-50 flex justify-end transition-all duration-200 ease-in-out"
      :class="isContactSidebarOpen ? 'w-full' : 'w-16'"
    >
      <!-- Toggle button -->
      <div
        v-on-click-outside="[
          closeMobileSidebar,
          { ignore: ['#contact-sidebar-content'] },
        ]"
        class="flex items-start p-1 w-fit h-fit relative order-1 xs:top-24 top-28 transition-all bg-n-solid-2 border border-n-weak duration-500 ease-in-out"
        :class="[
          isContactSidebarOpen
            ? 'justify-end ltr:rounded-l-full rtl:rounded-r-full ltr:rounded-r-none rtl:rounded-l-none'
            : 'justify-center rounded-full ltr:mr-6 rtl:ml-6',
        ]"
      >
        <Button
          ghost
          slate
          sm
          class="!rounded-full rtl:rotate-180"
          :class="{ 'bg-n-alpha-2': isContactSidebarOpen }"
          :icon="
            isContactSidebarOpen
              ? 'i-lucide-panel-right-close'
              : 'i-lucide-panel-right-open'
          "
          data-contact-sidebar-toggle
          @click="handleConversationSidebarToggle"
        />
      </div>

      <Transition
        enter-active-class="transition-transform duration-200 ease-in-out"
        leave-active-class="transition-transform duration-200 ease-in-out"
        enter-from-class="ltr:translate-x-full rtl:-translate-x-full"
        enter-to-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-from-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-to-class="ltr:translate-x-full rtl:-translate-x-full"
      >
        <div
          v-if="isContactSidebarOpen"
          id="contact-sidebar-content"
          class="order-2 w-[85%] sm:w-[50%] bg-n-solid-2 ltr:border-l rtl:border-r border-n-weak overflow-y-auto py-6 shadow-lg"
        >
          <slot name="sidebar" />
        </div>
      </Transition>
    </div>
  </section>
</template>
